import dayjs, { Dayjs } from "dayjs";
import {
  PricebookEffectiveMode,
  PricebookValidity,
  PricebookValidityRequest,
} from "../../../../types/price-book.type";
import { formatPeriodDay } from "../Periods/periodModel";
import { PriceBookFormErrors, PriceBookFormState } from "../types";

export type ValidityForm = Pick<
  PriceBookFormState,
  "effectiveMode" | "effectiveFrom" | "effectiveTo"
>;

export const EFFECTIVE_MODES: PricebookEffectiveMode[] = ["now", "date", "range"];

export const EFFECTIVE_MODE_LABEL: Record<PricebookEffectiveMode, string> = {
  now: "ทันที",
  date: "ล่วงหน้า",
  range: "ตามช่วงวันที่",
};

export const EFFECTIVE_MODE_HINT: Record<PricebookEffectiveMode, string> = {
  now: "ใช้ได้ทุกวันตั้งแต่บันทึก ไม่มีวันสิ้นสุด",
  date: "ใช้ได้ตั้งแต่วันที่เลือก (00:00 เวลาไทย) ไม่มีวันสิ้นสุด",
  range: "ใช้ได้เฉพาะวันเริ่มถึงวันสิ้นสุดที่เลือก",
};

/** Worded as the API words the same refusals. */
export const VALIDITY_MESSAGE = {
  fromRequired: "กรุณาเลือกวันเริ่มใช้",
  toRequired: "กรุณาเลือกวันสิ้นสุด",
  fromNotFuture: "วันที่เริ่มใช้ต้องหลังวันนี้",
  fromInPast: "วันที่เริ่มใช้ต้องไม่ก่อนวันนี้",
  dateOrder: "วันสิ้นสุดต้องไม่ก่อนวันเริ่ม",
  toInPast: "วันสิ้นสุดต้องไม่ก่อนวันนี้",
} as const;

/** Narrows a radio value back to an effective mode; anything else is not one. */
export const toEffectiveModeValue = (value: string): PricebookEffectiveMode | undefined =>
  EFFECTIVE_MODES.find((mode) => mode === value);

const toValidDay = (day: Dayjs | null): Dayjs | null =>
  day?.isValid() ? day : null;

/** An edit may keep a first day that has passed, so the last day of a running book can still move. */
const isStoredStart = (from: Dayjs, stored: PricebookValidity | null): boolean =>
  stored?.effective_from === formatPeriodDay(from);

const validateFrom = (
  form: ValidityForm,
  today: Dayjs,
  stored: PricebookValidity | null,
): string | undefined => {
  const from = toValidDay(form.effectiveFrom);
  if (!from) return VALIDITY_MESSAGE.fromRequired;
  if (isStoredStart(from, stored)) return undefined;
  if (form.effectiveMode === "date" && !from.isAfter(today, "day")) {
    return VALIDITY_MESSAGE.fromNotFuture;
  }
  if (form.effectiveMode === "range" && from.isBefore(today, "day")) {
    return VALIDITY_MESSAGE.fromInPast;
  }
  return undefined;
};

const validateTo = (form: ValidityForm, today: Dayjs): string | undefined => {
  const from = toValidDay(form.effectiveFrom);
  const to = toValidDay(form.effectiveTo);
  if (!to) return VALIDITY_MESSAGE.toRequired;
  if (from && to.isBefore(from, "day")) return VALIDITY_MESSAGE.dateOrder;
  if (to.isBefore(today, "day")) return VALIDITY_MESSAGE.toInPast;
  return undefined;
};

/**
 * What the API would refuse about the validity, checked before the save: `date` starts after
 * `today` (the Bangkok day), `range` runs from today on in order. `stored` is the book's validity
 * on an edit, null for a new book or a copy.
 */
export const validateValidity = (
  form: ValidityForm,
  today: Dayjs,
  stored: PricebookValidity | null,
): PriceBookFormErrors => {
  if (form.effectiveMode === "now") return {};
  const errors: PriceBookFormErrors = {};
  const fromError = validateFrom(form, today, stored);
  if (fromError) errors.effectiveFrom = fromError;
  if (form.effectiveMode === "range") {
    const toError = validateTo(form, today);
    if (toError) errors.effectiveTo = toError;
  }
  return errors;
};

/** The days the form asks for, as the book would store them. */
export const toRequestedValidity = (form: ValidityForm): PricebookValidity => {
  const from = toValidDay(form.effectiveFrom);
  const to = toValidDay(form.effectiveTo);
  return {
    effective_from: form.effectiveMode !== "now" && from ? formatPeriodDay(from) : null,
    effective_to: form.effectiveMode === "range" && to ? formatPeriodDay(to) : null,
  };
};

/** The mode and the days it needs, as the create, copy and PATCH bodies take them. */
export const buildValidityRequest = (form: ValidityForm): PricebookValidityRequest => {
  const requested = toRequestedValidity(form);
  return {
    effective_mode: form.effectiveMode,
    effective_from: requested.effective_from ?? undefined,
    effective_to: requested.effective_to ?? undefined,
  };
};

/** An edit sends the validity only when it moved, so a name change never re-checks stored days. */
export const isValidityChanged = (
  form: ValidityForm,
  stored: PricebookValidity,
): boolean => {
  const requested = toRequestedValidity(form);
  return (
    requested.effective_from !== stored.effective_from ||
    requested.effective_to !== stored.effective_to
  );
};

/**
 * The earliest first day the picker offers: tomorrow for ล่วงหน้า, today for a range — or the
 * stored first day an edit keeps, when that has already passed.
 */
export const resolveFromMinDate = (
  mode: PricebookEffectiveMode,
  today: Dayjs,
  stored: PricebookValidity | null,
): Dayjs => {
  const earliest = mode === "date" ? today.add(1, "day") : today;
  const storedFrom = stored?.effective_from ? dayjs(stored.effective_from) : null;
  return storedFrom?.isBefore(earliest, "day") ? storedFrom : earliest;
};
